import * as THREE from 'three';
import { hash3 } from '../fishSwim';
import type { FishSteer, PosableFish } from '../fishSchools';
import { FISH_LIVES } from '../../sim/fish';
import { REEF_DIETS } from '../../sim/predation';
import { TAKEN_BELOW_MM } from './data';
import { COUNTED_REEF_KINDS, FISH_KINDS } from '../fishKinds';
import { SEDIMENT } from './particles';

/**
 * The Reef's animals responding to one another and to the reef (M265, the user's brief saved as
 * docs/INTERACTIONS_BRIEF.md): one coordinator over the fish's swimming, steering a fish for a while
 * (`FishSchools.steer`) and handing it back. It draws behavior, never the ecology: every number of fish, every death and
 * every feeding relationship is the simulation's (`REEF_DIETS` the predators and their prey, `TAKEN_BELOW_MM` the sizes
 * they take), and nothing here is told back to it (the brief's sections 3 and 12).
 *
 * **Priorities** (the brief's section 1): `PRIORITY`, the lower first. An animal holds one interaction at a time; a
 * higher one takes it from a lower one where it begins (handed over from where it is drawn, at the blend it had, so no
 * jump), a lower one waits. A hunt (`hunts.ts`, priority 2) is the hunts' own: a fish a hunt holds is given back to its
 * school here and left to the hunt's pose (one owner of its drawing at a time).
 *
 * **Its clock** is the animation's (`seconds`, at most `LIFE.stepS` a frame), not the game's: a day of five seconds
 * neither starts nor ends any of it (the brief's section 9), and a frame of the player's pause still draws it (M95).
 * Each animal's draws are its own (`hash3` of its name), so the same fish does the same at the same seconds.
 *
 * Every setting below is a rendering choice, JUDGED by eye on a phone and a computer at the Reef's home view (the
 * brief's "artistic starting points, not biological measurements"); the behaviors' kinds and who does them are cited
 * where they are each set.
 */

/** An interaction's kind, and its priority, the lower first (the brief's section 1; 2 is a hunt's, `hunts.ts`). */
export type Behavior = 'flee' | 'shelter' | 'rest' | 'chase' | 'chased' | 'feed';
export const PRIORITY: Readonly<Record<Behavior, number>> = { flee: 1, shelter: 3, rest: 3, chase: 4, chased: 4, feed: 5 };

/** What a fish feeds on, and the kinds of the reef's places the interactions know (`LifeAnchors`). */
export type Substrate = 'turf' | 'macro' | 'sand' | 'shelter';

/**
 * How each herbivore's and the goatfish's feeding is drawn (the brief's section 2), its bites and where:
 * - `scrape`, the parrotfish (bullethead, palenose, redlip): "excavating" and "scraping" feeding on turf-covered dead
 *   coral and pavement, the bite leaving a scar (Bellwood and Choat 1990, *Env. Biol. Fish.* 28: 189, the parrotfishes'
 *   two modes; not read here, the brief's own words for it): two or three quick forward bites on the hard bottom
 *   (`turf`), tilted nose down, a pause, and on to another.
 * - `nibble`, the yellow tang and the manini, grazers of the turf's filaments: many small quick bites over the hard
 *   bottom, hopping short ways between them.
 * - `browse`, the kala and the nenue, browsers of the upright brown and red algae (`macro`, the limu drawn: the
 *   *Dictyota*, *Padina* and the rest, `reefScene.ts`'s `REEF_LIMU`): slower, a few deliberate bites at a plant, on along
 *   the plants.
 * - `probe`, the moano kea, a goatfish feeling the sand with its barbels for the animals in it: low over the sand
 *   (`sand`), short jabs forward and side to side, on a little way, again.
 * The roi, the ulua and the ʻōmilu feed only through the hunts (the brief: "Do not use herbivore-style grazing
 * animations for roi, ulua, or ʻōmilu"); the taʻape (a night feeder over the sand, schooled by day) and the moray none.
 * Each style's `bites` its bites at a place, `biteS` each one's seconds, `reachBL` its reach forward (lengths),
 * `pauseS` after, `moreOdds` the odds of another place within `nextM`, at most `places`, `tilt` its nose down
 * (radians), `gapS` the mean seconds between its bouts, `speedBL` its approach (lengths a second): JUDGED, the bouts'
 * share of a fish's day out about the third its school's dives had (`schooling.ts`'s `FEED_FOR_S` of `FEED_EVERY_S`).
 */
export type FeedStyle = 'scrape' | 'nibble' | 'browse' | 'probe';
export const FEED_STYLES: Readonly<Record<FeedStyle, { substrate: Substrate; bites: readonly [number, number]; biteS: number; reachBL: number; sideBL: number; pauseS: readonly [number, number]; moreOdds: number; nextM: number; places: number; tilt: number; gapS: number; speedBL: number }>> = {
  scrape: { substrate: 'turf', bites: [2, 3], biteS: 0.32, reachBL: 0.16, sideBL: 0, pauseS: [0.6, 1.4], moreOdds: 0.5, nextM: 1.2, places: 3, tilt: 0.35, gapS: 9, speedBL: 1.2 },
  nibble: { substrate: 'turf', bites: [4, 8], biteS: 0.16, reachBL: 0.07, sideBL: 0, pauseS: [0.3, 0.8], moreOdds: 0.7, nextM: 0.5, places: 4, tilt: 0.2, gapS: 4, speedBL: 1 },
  browse: { substrate: 'macro', bites: [2, 4], biteS: 0.5, reachBL: 0.1, sideBL: 0, pauseS: [0.8, 1.6], moreOdds: 0.45, nextM: 1.5, places: 3, tilt: 0.12, gapS: 10, speedBL: 0.6 },
  probe: { substrate: 'sand', bites: [3, 5], biteS: 0.3, reachBL: 0.1, sideBL: 0.12, pauseS: [0.4, 1], moreOdds: 0.6, nextM: 0.9, places: 3, tilt: 0.3, gapS: 7, speedBL: 0.8 },
};
export const FEEDERS: Readonly<Record<string, FeedStyle>> = { bullethead: 'scrape', palenose: 'scrape', redlip: 'scrape', yellowTang: 'nibble', manini: 'nibble', kala: 'browse', nenue: 'browse', moanokea: 'probe' };

/**
 * The predators fish watch for and their prey, the simulation's own (`REEF_DIETS`): the roi, the ulua and the ʻōmilu,
 * and only their prey take fright, a prey of a size they take (`TAKEN_BELOW_MM`, or at any size where the simulation
 * has it so, `takenAtEverySize`) or young (the brief's "Avoid having every fish react to every large animal").
 */
export const PREY_OF: ReadonlyMap<string, ReadonlySet<string>> = (() => {
  const m = new Map<string, Set<string>>();
  for (const d of REEF_DIETS) {
    const set = m.get(d.predator) ?? new Set<string>();
    for (const p of d.prey) if (p !== 'octopus') set.add(p);
    m.set(d.predator, set);
  }
  return m;
})();
/** The octopus's predators among them, the simulation's (the ulua's, M129): only these startle it (review M267's: every predator did, the roi and the ʻōmilu with no octopus in their diets). */
export const OCTOPUS_PREDATORS: ReadonlySet<string> = new Set(REEF_DIETS.filter((d) => d.prey.includes('octopus')).map((d) => d.predator));
const TAKEN_AT_EVERY_SIZE: ReadonlySet<string> = new Set(FISH_LIVES.filter((l) => (l as { takenAtEverySize?: boolean }).takenAtEverySize).map((l) => l.id));

/**
 * How a prey notices and flees (the brief's section 3), JUDGED: a predator seen within `noticeLengths` of its own drawn
 * lengths, at least `noticeLeastM`, as it closes (its way toward the prey, `closingCos` of it at least) or nearer than
 * `closeShare` of that whatever its way; each prey's own wariness `wary[0]` to `wary[1]` times that, a young fish's
 * `youngWary` more; a line of sight over the reef (`clearSamples` of it). Fled `fleeBL` of its own lengths away over
 * `dartS`, off the straight line by up to `jitterRad` (no two alike), held there `holdS`, back to its school over
 * `regroupS`; the school's others within `alarmBL` of it fleeing `alarmS` after (the fright passed through the school);
 * none again for `restS`. A young fish, or `shelterOdds` of the others, takes shelter where it has one within
 * `shelterM`.
 */
export const WARINESS = { noticeLengths: 2, noticeLeastM: 1.5, closingCos: 0.3, closeShare: 0.5, wary: [0.7, 1.3], youngWary: 1.3, clearSamples: 3, fleeBL: [2, 4], dartS: [0.45, 0.75], jitterRad: 0.6, holdS: [0.6, 1.4], regroupS: [1.5, 3], alarmBL: 3, alarmS: [0.1, 0.4], restS: 4, shelterOdds: 0.35, shelterM: 3, most: 40 } as const;

/**
 * Shelter (the brief's section 4), JUDGED: swum to over `goS`, kept in at least `stayS` and while its predator is within
 * `untilLengths` of its notice, out again over `outS`; its place `insideShare` of the shelter's width behind its middle
 * from the threat (in among the branches, or under the rock's edge), so the coral or the rock hides some of it. By
 * night (`awake` under `nightAwake`), a share `nightOdds` of the young and of the parrotfish (which sleep in the reef's
 * holes) rest in one within `nightM` of where they are, at most `crowd` to a shelter, out again as they wake past
 * `wakeAwake`; at most `nightMost` at once.
 */
export const SHELTER = { goS: [0.8, 1.4], stayS: [3, 8], untilLengths: 1.5, outS: [1.5, 2.5], insideShare: 0.25, nightAwake: 0.25, wakeAwake: 0.35, nightOdds: 0.5, nightM: 4, crowd: 3, nightMost: 60 } as const;
/**
 * The bites that lift the sediment (`particles.ts`): the parrotfish's (`SEDIMENT.grazers`, M173's) and, since M265, the
 * goatfish's probing of the sand; the tangs' and the browsers' nibbles none.
 */
const LIFTS: ReadonlySet<string> = new Set([...SEDIMENT.grazers, 'moanokea']);
const NIGHT_SHELTERERS: ReadonlySet<string> = new Set(['bullethead', 'palenose', 'redlip']);
/** The kinds that rest in the open, schooled (the taʻape by day, `Swim.restsInOpen`): never sent into shelter to rest, young or not. */
const OPEN_RESTERS: ReadonlySet<string> = new Set([...FISH_KINDS, ...COUNTED_REEF_KINDS].filter((k) => k.swim.restsInOpen).map((k) => k.species));

/**
 * The terminal males' territories (the brief's section 6), the bullethead's and the palenose's, whose males the sources
 * `schooling.ts` cites hold them (Tricas and Boyle 2014; Annandale et al. 2024): a male awake and out notices a rival (a
 * terminal male of his kind from another school, or at `initialOdds` a fish of its first phase) within `noticeLengths`
 * of his lengths, turns and chases it `chaseBL` past where it was over `chaseS`, the rival fleeing `fleeBL` after
 * `fleeAfterS`; both back to their own over `outS`; a male again no sooner than `againS` (each his own, JUDGED: now and
 * then, not all the time).
 */
export const TERRITORY = { species: ['bullethead', 'palenose'], noticeLengths: 2.5, initialOdds: 0.3, chaseBL: 0.5, chaseS: 0.7, fleeBL: [2, 3], fleeAfterS: 0.15, outS: 1.2, againS: [20, 45] } as const;

/** The coordinator's own: its longest frame, s; how often it looks about (detection), s; how far from the camera it starts anything, m. */
export const LIFE = { stepS: 0.25, lookS: 0.25, withinM: 25 } as const;

/** A fish as the interactions see it: as a hunt does (`PosableFish`), with its group (`hunts.ts`'s). */
export interface LifeFish extends PosableFish {
  group: 'young' | 'initial' | 'terminal';
}

/** A place of the reef an animal can use (`LifeAnchors`): where, its width, and its kind. */
export interface Anchor {
  x: number;
  y: number;
  z: number;
  widthM: number;
  substrate: Substrate;
  /** What it is, for the debug display (a coral's form, the rock, the sand, a plant's name). */
  name: string;
}

/** The reef's places by kind, each kind kept in cells of `cellM` for a near one to be found without looking at all (the brief's section 11). */
export class LifeAnchors {
  private readonly cells = new Map<string, Anchor[]>();
  /** Each place's use by where it is, not by its object: a day's anchors laid again keep the uses of the acts under way (review M267's: counted afresh, a full shelter took `crowd` more). */
  private readonly used = new Map<string, number>();
  readonly count: Readonly<Record<Substrate, number>>;

  constructor(anchors: readonly Anchor[], private readonly cellM = 2) {
    const count: Record<Substrate, number> = { turf: 0, macro: 0, sand: 0, shelter: 0 };
    for (const a of anchors) {
      const key = this.key(a.substrate, Math.floor(a.x / cellM), Math.floor(a.z / cellM));
      const list = this.cells.get(key) ?? [];
      list.push(a);
      this.cells.set(key, list);
      count[a.substrate]++;
    }
    this.count = count;
  }

  private key(s: Substrate, i: number, k: number): string {
    return `${s}:${i}:${k}`;
  }

  /**
   * A place of `substrate` within `withinM` of (x, z), by a draw `u` (0 to 1) among the nearest few (so a school's fish do
   * not all go to one), none used more than `most` at once; null where none.
   */
  near(substrate: Substrate, x: number, z: number, withinM: number, u: number, most = Infinity, skip: Anchor | null = null): Anchor | null {
    const r = Math.ceil(withinM / this.cellM);
    const [ci, ck] = [Math.floor(x / this.cellM), Math.floor(z / this.cellM)];
    const found: [number, Anchor][] = [];
    for (let i = ci - r; i <= ci + r; i++) {
      for (let k = ck - r; k <= ck + r; k++) {
        for (const a of this.cells.get(this.key(substrate, i, k)) ?? []) {
          const d = Math.hypot(a.x - x, a.z - z);
          if (d <= withinM && a !== skip && (this.used.get(this.at(a)) ?? 0) < most) found.push([d, a]);
        }
      }
    }
    if (found.length === 0) return null;
    found.sort((p, q) => p[0] - q[0]);
    const few = found.slice(0, 4);
    return (few[Math.min(few.length - 1, Math.floor(u * few.length))] as [number, Anchor])[1];
  }

  /** One more, or one fewer, at a place (its use counted for `near`'s `most`). */
  use(a: Anchor, by: 1 | -1): void {
    const at = this.at(a);
    const n = Math.max(0, (this.used.get(at) ?? 0) + by);
    if (n > 0) this.used.set(at, n);
    else this.used.delete(at);
  }

  /** The uses of the places laid before, kept where the same places are laid again. */
  carry(from: LifeAnchors | null): this {
    for (const [at, n] of from?.used ?? []) this.used.set(at, n);
    return this;
  }

  private at(a: Anchor): string {
    return `${a.substrate}:${a.x}:${a.z}`;
  }
}

/** A point of an interaction's path: when, s from its start, and where its fish's foot is to be. */
interface Key {
  t: number;
  x: number;
  y: number;
  z: number;
}

/** A bite, a jab or a peck: from `t` for `s` seconds, its fish thrust `reach` m along its way and `side` m across it and back. */
interface Bite {
  t: number;
  s: number;
  reach: number;
  side: number;
}

/** An interaction under way. */
interface Act {
  id: string;
  species: string;
  kind: Behavior;
  priority: number;
  t: number;
  /** Seconds its path lasts (its keys' last), then `outS` back to its school. */
  span: number;
  outS: number;
  inS: number;
  /** The blend it began from (a lower one's it took the fish from), and its fish's own drawn share as it began (`FishSteer.hold`). */
  from: number;
  hold: number;
  keys: Key[];
  bites: Bite[];
  /** Its way along the level while it bites (its nose to the place), x and z; and each place's own, while it is there. */
  way: [number, number];
  faces: { from: number; to: number; way: [number, number] }[];
  tilt: number;
  /** The fish, the anchor or the rival it is about, for the debug display; and the anchor it holds a use of. */
  target: string | null;
  threat: string | null;
  feature: string | null;
  anchor: Anchor | null;
  /** Its predator's name and the notice it had (shelter), kept in while it is near. */
  watch: { id: string; withinM: number } | null;
  /** The bites' times for the sediment (`LifeWorld.bite`), each sent once. */
  told: number;
  /** For a feed: the places it may go on to, and has. */
  places: number;
  style: FeedStyle | null;
  /** For a night's rest: kept while its school sleeps. */
  night: boolean;
  /** A developer's (the brief's section 13): started outside the rules, counting nothing. */
  staged: 'life' | 'developer';
}

/** An interaction as the debug display shows it (the brief's section 13). */
export interface LifeDebug {
  id: string;
  species: string;
  behavior: Behavior;
  priority: number;
  target: string | null;
  position: readonly [number, number, number];
  remainingS: number;
  threat: string | null;
  feature: string | null;
  staged: 'life' | 'developer';
}

/** What the interactions are told of the reef each frame, and how they steer and tell: the scene's own (`ReefScene`). */
export interface LifeWorld {
  fish(): readonly LifeFish[];
  steer(id: string, steer: FishSteer | null): void;
  /** The animals a hunt holds now (`ReefHunts.busy`), left to it. */
  busy(): ReadonlySet<string>;
  groundAt(x: number, z: number): number;
  anchors(): LifeAnchors | null;
  /** A bite at the reef (a parrotfish's or a goatfish's), for the sediment it lifts (`particles.ts`): the kind's, the point's, its own key. */
  bite(kind: number, x: number, y: number, z: number, key: number): void;
  /** The octopus's predators seen (`OCTOPUS_PREDATORS`), where they are, for its `startle` (`octopuses.ts`). */
  threats(near: readonly { species: string; at: THREE.Vector3; width: number }[]): void;
}

/** A name's own number, for its draws. */
export function idSalt(id: string): number {
  let h = 2_166_136_261;
  for (let i = 0; i < id.length; i++) h = Math.imul(h ^ id.charCodeAt(i), 16_777_619);
  return h >>> 0;
}

const between = (r: readonly [number, number], u: number): number => r[0] + (r[1] - r[0]) * u;
/** How far over the reef a fish's path is kept until it comes down to its place, m (JUDGED: a fish's height, about). */
const CLEAR_M = 0.1;
/**
 * The fastest a fish's path rises or sinks, its lengths a second at an eased leg's quickest (half again its mean): its
 * school's own `fishSchools.ts`'s `RISE_BL_PER_S` (0.6) twice over, JUDGED, a fish going down to bite or up to rejoin
 * quicker than its school's drift, still under M159's limit on the rise and sink seen (2.25 of its lengths a second at
 * the 99th centile, `tests/reefFishMotion.test.ts`).
 */
const RISE_BL_PER_S = 1.2;
const EASE_PEAK = 1.5;
/** Its way back to its school's place on the mean, its lengths a second (JUDGED: about its school's commuting pace, `schooling.ts`'s `COMMUTE_BL_PER_S`). */
const RETURN_BL_PER_S = 1.2;
const ease = (u: number): number => u * u * (3 - 2 * u);

/** The Reef's interactions. */
export class ReefLife {
  private clock = 0;
  private sinceLook = Infinity;
  private readonly acts = new Map<string, Act>();
  /** Each feeder's next bout, on the clock, by its name. */
  private readonly nextFeed = new Map<string, number>();
  /** Each fish's quiet until (none started on it before), by its name: a prey after a fright, a male after a chase. */
  private readonly quietUntil = new Map<string, number>();
  /** Frights passed through a school, still to start: when, whom, from where. */
  private alarms: { at: number; id: string; from: THREE.Vector3; threat: string }[] = [];
  /** Each predator's place at the last look, for its way. */
  private readonly lastSeen = new Map<string, THREE.Vector3>();
  private serial = 0;
  private readonly tmp = new THREE.Vector3();

  constructor(private readonly world: LifeWorld) {}

  /**
   * A frame of `seconds` on the animation's clock: each interaction under way moved on and its fish steered; and, every
   * `LIFE.lookS`, the reef looked over for new ones among the fish in view within `LIFE.withinM` of the camera (`visible`
   * the part of the canvas the panels leave). None begun when `calm` (a player who asks for less motion), those under
   * way let finish.
   */
  update(camera: THREE.Camera, seconds: number, calm: boolean, visible: readonly [number, number, number, number] = [-1, 1, -1, 1]): void {
    const dt = Number.isFinite(seconds) && seconds > 0 ? Math.min(seconds, LIFE.stepS) : 0;
    this.clock += dt;
    this.sinceLook += dt;
    const looks = !calm && this.sinceLook >= LIFE.lookS;
    // Nothing under way and no look due (every frame when `calm`): the fish not asked for.
    if (this.acts.size === 0 && !looks) return;
    const fish = this.world.fish();
    const byId = new Map(fish.map((f) => [f.id, f]));
    const busy = this.world.busy();
    for (const a of [...this.acts.values()]) this.step(a, dt, byId, busy);
    if (!looks) return;
    this.sinceLook = 0;
    this.look(camera, fish, byId, busy, visible);
  }

  /** An interaction moved on by `dt` and its fish steered; ended where it is done, its fish gone, or a hunt holds it. */
  private step(a: Act, dt: number, byId: ReadonlyMap<string, LifeFish>, busy: ReadonlySet<string>): void {
    const f = byId.get(a.id);
    if (!f || busy.has(a.id)) {
      this.end(a);
      return;
    }
    a.t += dt;
    // Kept in shelter while its predator is near, or while its school sleeps (`SHELTER`).
    if (a.watch && a.t > a.span - 0.05) {
      const p = byId.get(a.watch.id);
      if (p && p.at.distanceTo(f.at) < a.watch.withinM) a.t = a.span - 0.05;
      else a.watch = null;
    }
    if (a.night && a.t > a.span - 0.05 && (f.awake ?? 1) < SHELTER.wakeAwake) a.t = a.span - 0.05;
    if (a.t >= a.span + a.outS) {
      this.end(a);
      return;
    }
    // Its bites' sediment (the parrotfish's and the goatfish's), each once, at the bite's foot.
    while (a.told < a.bites.length && (a.bites[a.told] as Bite).t + (a.bites[a.told] as Bite).s / 2 <= a.t) {
      const k = this.keyAt(a, a.t);
      if (LIFTS.has(a.species)) this.world.bite(f.kind, k.x, this.world.groundAt(k.x, k.z), k.z, (idSalt(a.id) + a.told * 7_919 + this.serial) | 0);
      a.told++;
    }
    const at = this.keyAt(a, Math.min(a.t, a.span));
    // Its bite: along its way and across it, out and back over the bite (a sine's half).
    for (const b of a.bites) {
      if (a.t < b.t || a.t > b.t + b.s) continue;
      const u = Math.sin((Math.PI * (a.t - b.t)) / b.s);
      at.x += (a.way[0] * b.reach - a.way[1] * b.side) * u;
      at.z += (a.way[1] * b.reach + a.way[0] * b.side) * u;
    }
    const blendIn = a.inS > 0 ? Math.min(1, a.from + (1 - a.from) * ease(Math.min(1, a.t / a.inS))) : 1;
    const out = a.t > a.span ? 1 - ease(Math.min(1, (a.t - a.span) / Math.max(1e-6, a.outS))) : 1;
    const steer: FishSteer = { x: at.x, y: at.y, z: at.z, blend: blendIn * out, hold: a.hold };
    if (a.tilt) steer.tilt = a.tilt * (a.bites.some((b) => a.t >= b.t - 0.4 && a.t <= b.t + b.s + 0.4) ? 1 : 0.4);
    // Its nose to its food while it is at a place, the way it swims between (M265's: faced to its first place throughout,
    // it swam to the next and back to its school tail first).
    const here = a.faces.find((p) => a.t >= p.from && a.t <= p.to);
    if (here) steer.face = here.way;
    this.world.steer(a.id, steer);
  }

  /** Where an interaction's path has its fish at `t`, eased between its keys. */
  private keyAt(a: Act, t: number): { x: number; y: number; z: number } {
    const keys = a.keys;
    if (t <= (keys[0] as Key).t) return { ...(keys[0] as Key) };
    for (let k = 1; k < keys.length; k++) {
      const [p, q] = [keys[k - 1] as Key, keys[k] as Key];
      if (t <= q.t) {
        const u = ease((t - p.t) / Math.max(1e-6, q.t - p.t));
        return { x: p.x + (q.x - p.x) * u, y: p.y + (q.y - p.y) * u, z: p.z + (q.z - p.z) * u };
      }
    }
    return { ...(keys[keys.length - 1] as Key) };
  }

  /**
   * A height over the reef all the way from (x0, z0) to (x1, z1), at least `y`: the ground's highest along it (eight
   * samples) and `CLEAR_M` (M265's: a path drawn straight at its place, over a rock's edge the fish was set onto the rock
   * at once, 10.5 of its lengths up in a second).
   */
  private over(x0: number, z0: number, x1: number, z1: number, y: number, clear = CLEAR_M): number {
    let top = y;
    for (let k = 0; k <= 8; k++) top = Math.max(top, this.world.groundAt(x0 + ((x1 - x0) * k) / 8, z0 + ((z1 - z0) * k) / 8) + clear);
    return top;
  }

  /**
   * Its legs stretched where one would rise or sink faster than `RISE_BL_PER_S` (its bites after it later with it), and
   * its swim back to its school as long as the way up from its path's end needs (M265's: a fish was set down to its bite
   * 5 of its lengths a second).
   */
  private paced(a: Act, f: LifeFish): Act {
    const most = RISE_BL_PER_S * Math.max(0.05, f.width);
    for (let i = 1; i < a.keys.length; i++) {
      const [p, q] = [a.keys[i - 1] as Key, a.keys[i] as Key];
      const need = (EASE_PEAK * Math.abs(q.y - p.y)) / most;
      const extra = need - (q.t - p.t);
      if (extra <= 0) continue;
      const from = q.t;
      for (let j = i; j < a.keys.length; j++) (a.keys[j] as Key).t += extra;
      for (const b of a.bites) if (b.t >= from - 1e-9) b.t += extra;
      for (const p of a.faces) {
        if (p.from >= from - 1e-9) p.from += extra;
        if (p.to >= from - 1e-9) p.to += extra;
      }
    }
    a.span = (a.keys[a.keys.length - 1] as Key).t;
    const end = a.keys[a.keys.length - 1] as Key;
    const back = Math.hypot(end.x - f.at.x, end.z - f.at.z) / (RETURN_BL_PER_S * Math.max(0.05, f.width));
    a.outS = Math.max(a.outS, (EASE_PEAK * Math.abs(end.y - f.at.y)) / most, back);
    return a;
  }

  private end(a: Act): void {
    this.acts.delete(a.id);
    if (a.anchor) this.world.anchors()?.use(a.anchor, -1);
    this.world.steer(a.id, null);
  }

  /**
   * Begun where the fish holds none, or a lower one it takes over (handed on from where the fish is drawn, at its blend):
   * whether it was begun.
   */
  private begin(a: Act): boolean {
    const was = this.acts.get(a.id);
    if (was && was.priority <= a.priority) {
      if (a.anchor) this.world.anchors()?.use(a.anchor, -1);
      return false;
    }
    if (was) {
      const blend = was.t < was.inS ? was.from + (1 - was.from) * ease(was.t / Math.max(1e-6, was.inS)) : was.t > was.span ? 1 - ease(Math.min(1, (was.t - was.span) / Math.max(1e-6, was.outS))) : 1;
      a.from = Math.max(a.from, blend);
      if (was.anchor) this.world.anchors()?.use(was.anchor, -1);
      a.hold = Math.max(a.hold, was.hold);
    }
    this.serial++;
    this.acts.set(a.id, a);
    return true;
  }

  private act(f: LifeFish, kind: Behavior, keys: Key[], staged: 'life' | 'developer'): Act {
    return {
      id: f.id, species: f.species, kind, priority: PRIORITY[kind], t: 0, span: keys[keys.length - 1]?.t ?? 0, outS: 1, inS: 0.4, from: 0, hold: f.drawn ?? f.shown,
      keys, bites: [], way: [f.right ? 1 : -1, 0], faces: [], tilt: 0, target: null, threat: null, feature: null, anchor: null, watch: null, told: 0, places: 0, style: null, night: false, staged,
    };
  }

  /** The reef looked over: frights, chases, night shelter and feeding begun among the fish in view. */
  private look(camera: THREE.Camera, fish: readonly LifeFish[], byId: ReadonlyMap<string, LifeFish>, busy: ReadonlySet<string>, visible: readonly [number, number, number, number]): void {
    const seen = fish.filter((f) => (f.drawn ?? f.shown) >= 0.5 && !busy.has(f.id) && this.inView(camera, f.at, visible));
    const cells = new Map<string, LifeFish[]>();
    const cellOf = (x: number, z: number): string => `${Math.floor(x / 2)}:${Math.floor(z / 2)}`;
    for (const f of seen) {
      const k = cellOf(f.at.x, f.at.z);
      const list = cells.get(k) ?? [];
      list.push(f);
      cells.set(k, list);
    }
    const around = (x: number, z: number, r: number): LifeFish[] => {
      const out: LifeFish[] = [];
      const n = Math.ceil(r / 2);
      const [ci, ck] = [Math.floor(x / 2), Math.floor(z / 2)];
      for (let i = ci - n; i <= ci + n; i++) for (let k = ck - n; k <= ck + n; k++) out.push(...(cells.get(`${i}:${k}`) ?? []));
      return out;
    };
    // The predators' ways since the last look.
    const threats: { species: string; at: THREE.Vector3; width: number }[] = [];
    for (const p of fish) {
      if (!PREY_OF.has(p.species) || p.group === 'young') continue;
      const last = this.lastSeen.get(p.id);
      const way = last ? this.tmp.copy(p.at).sub(last) : this.tmp.set(0, 0, 0);
      (this.lastSeen.get(p.id) ?? this.lastSeen.set(p.id, new THREE.Vector3()).get(p.id) as THREE.Vector3).copy(p.at);
      if ((p.drawn ?? p.shown) < 0.5) continue;
      // The octopus's own predators told to it (review M267's: the roi and the ʻōmilu had startled it, none eats it).
      if (OCTOPUS_PREDATORS.has(p.species)) threats.push({ species: p.species, at: p.at, width: p.width });
      this.frights(p, way.clone(), around);
    }
    this.world.threats(threats);
    // Frights passed through schools, as they come due.
    const due = this.alarms.filter((x) => x.at <= this.clock);
    this.alarms = this.alarms.filter((x) => x.at > this.clock);
    for (const x of due) {
      const f = byId.get(x.id);
      if (f && !busy.has(f.id) && !this.fled(f.id)) this.flee(f, x.from, x.threat, 'life', false);
    }
    this.chases(seen, around);
    this.nights(seen);
    this.feeds(seen);
  }

  /** Whether a point is in the part of the screen the panels leave and within `LIFE.withinM` of the camera (the brief's section 10). */
  private inView(camera: THREE.Camera, at: THREE.Vector3, visible: readonly [number, number, number, number]): boolean {
    if (at.distanceTo(camera.position) > LIFE.withinM) return false;
    const p = this.tmp.copy(at).project(camera);
    return p.z < 1 && p.x >= visible[0] && p.x <= visible[1] && p.y >= visible[2] && p.y <= visible[3];
  }

  /** Whether a prey takes fright at a predator: its prey, of a size it takes (or young), near, as it closes, in sight over the reef (`WARINESS`). */
  wary(p: LifeFish, way: THREE.Vector3, q: LifeFish): boolean {
    const W = WARINESS;
    if (!PREY_OF.get(p.species)?.has(q.species)) return false;
    if (q.group !== 'young' && !TAKEN_AT_EVERY_SIZE.has(q.species) && !(q.tlMm < TAKEN_BELOW_MM)) return false;
    const salt = idSalt(q.id);
    const wariness = between(W.wary, hash3(salt, 0, 2_660)) * (q.group === 'young' ? W.youngWary : 1);
    const notice = Math.max(W.noticeLeastM, W.noticeLengths * p.width) * wariness;
    const dx = q.at.x - p.at.x;
    const dz = q.at.z - p.at.z;
    const d = Math.hypot(dx, dz, q.at.y - p.at.y);
    if (d > notice) return false;
    const speed = Math.hypot(way.x, way.z);
    const closing = speed > 1e-6 && (way.x * dx + way.z * dz) / (speed * Math.max(1e-6, Math.hypot(dx, dz))) >= W.closingCos;
    if (!closing && d > W.closeShare * notice) return false;
    // In sight: the reef nowhere along the way between standing over both.
    for (let k = 1; k <= W.clearSamples; k++) {
      const u = k / (W.clearSamples + 1);
      const ground = this.world.groundAt(p.at.x + dx * u, p.at.z + dz * u);
      if (ground > Math.max(p.at.y, q.at.y) + 0.05) return false;
    }
    return true;
  }

  /** Each prey near a predator that takes fright (`wary`), fleeing; its school's near ones after it. */
  private frights(p: LifeFish, way: THREE.Vector3, around: (x: number, z: number, r: number) => LifeFish[]): void {
    const W = WARINESS;
    const reach = Math.max(W.noticeLeastM, W.noticeLengths * p.width) * W.wary[1] * W.youngWary;
    for (const q of around(p.at.x, p.at.z, reach)) {
      if (q.id === p.id || (this.quietUntil.get(q.id) ?? -Infinity) > this.clock) continue;
      if (this.fled(q.id)) continue;
      if (this.fleeing() >= W.most || !this.wary(p, way, q)) continue;
      if (!this.flee(q, p.at, p.id, 'life', true)) continue;
      // The fright through its school: its near schoolmates after it, each a moment later.
      for (const m of around(q.at.x, q.at.z, W.alarmBL * q.width)) {
        if (m.school !== q.school || m.id === q.id || this.acts.get(m.id)?.kind === 'flee' || this.alarms.some((x) => x.id === m.id)) continue;
        if (q.at.distanceTo(m.at) > W.alarmBL * q.width) continue;
        this.alarms.push({ at: this.clock + between(W.alarmS, hash3(idSalt(m.id), this.serial, 2_661)), id: m.id, from: p.at.clone(), threat: p.id });
      }
    }
  }

  /** Already fled, or hidden in a shelter (review M267's: one held in by its watch outlasted its quiet, or a schoolmate's alarm came, and it fled out into the open). */
  private fled(id: string): boolean {
    const a = this.acts.get(id);
    return !!a && (a.priority <= PRIORITY.flee || a.kind === 'shelter');
  }

  private fleeing(): number {
    let n = 0;
    for (const a of this.acts.values()) if (a.kind === 'flee' || (a.kind === 'shelter' && !a.night)) n++;
    return n;
  }

  /**
   * A fish fled from a threat at `from` (`WARINESS`): darted away off the straight line, then sheltered where it has one
   * near (a young one, or by its odds), else held and back to its school. Whether it was begun.
   */
  flee(f: LifeFish, from: THREE.Vector3, threat: string | null, staged: 'life' | 'developer', first: boolean): boolean {
    const W = WARINESS;
    const salt = idSalt(f.id) + this.serial;
    let [ax, az] = [f.at.x - from.x, f.at.z - from.z];
    const len = Math.hypot(ax, az);
    [ax, az] = len > 1e-6 ? [ax / len, az / len] : [1, 0];
    const turn = (hash3(salt, 1, 2_662) * 2 - 1) * W.jitterRad;
    const [ux, uz] = [ax * Math.cos(turn) - az * Math.sin(turn), ax * Math.sin(turn) + az * Math.cos(turn)];
    const far = between(W.fleeBL, hash3(salt, 2, 2_662)) * f.width;
    const anchors = this.world.anchors();
    const shelters = f.group === 'young' || hash3(salt, 4, 2_662) < W.shelterOdds;
    const shelter = shelters && anchors ? anchors.near('shelter', f.at.x + ux * far * 0.5, f.at.z + uz * far * 0.5, W.shelterM, hash3(salt, 5, 2_662), SHELTER.crowd) : null;
    return this.flight(f, shelter, [ux, uz], far, threat, staged, first, salt);
  }

  /**
   * A flight begun: into `shelter` where given (in among it from the threat's side, kept in while the threat is near),
   * else darted `far` m along `way`, held and back. Whether it was begun.
   */
  private flight(f: LifeFish, shelter: Anchor | null, [ux, uz]: [number, number], far: number, threat: string | null, staged: 'life' | 'developer', first: boolean, salt = idSalt(f.id) + this.serial): boolean {
    const W = WARINESS;
    const anchors = this.world.anchors();
    const dart = between(W.dartS, hash3(salt, 3, 2_662));
    const a = this.act(f, shelter ? 'shelter' : 'flee', [], staged);
    // Fled, its first key where it is drawn now.
    const start: Key = { t: 0, x: f.at.x, y: f.at.y, z: f.at.z };
    if (shelter) {
      anchors?.use(shelter, 1);
      const inside = SHELTER.insideShare * shelter.widthM;
      const [sx, sz] = [shelter.x + ux * inside, shelter.z + uz * inside];
      const go = between(SHELTER.goS, hash3(salt, 6, 2_662));
      const stay = between(SHELTER.stayS, hash3(salt, 7, 2_662));
      // Over the reef to it, then down into it.
      const above = this.over(f.at.x, f.at.z, sx, sz, f.at.y);
      a.keys = [start, { t: 0.7 * go, x: sx, y: above, z: sz }, { t: go, x: sx, y: shelter.y + 0.05, z: sz }, { t: go + stay, x: sx, y: shelter.y + 0.05, z: sz }];
      a.span = go + stay;
      a.outS = between(SHELTER.outS, hash3(salt, 8, 2_662));
      a.anchor = shelter;
      a.feature = shelter.name;
      if (threat) a.watch = { id: threat, withinM: SHELTER.untilLengths * Math.max(W.noticeLeastM, W.noticeLengths * f.width) };
    } else {
      const hold = between(W.holdS, hash3(salt, 9, 2_662));
      const [x, z] = [f.at.x + ux * far, f.at.z + uz * far];
      const y = this.over(f.at.x, f.at.z, x, z, f.at.y);
      a.keys = [start, { t: dart, x, y, z }, { t: dart + hold, x, y, z }];
      a.span = dart + hold;
      a.outS = between(W.regroupS, hash3(salt, 10, 2_662));
    }
    // Its first flight quicker in than a schoolmate's after it.
    a.inS = first ? 0.15 : 0.25;
    a.way = [ux, uz];
    a.threat = threat;
    if (!this.begin(this.paced(a, f))) return false;
    this.quietUntil.set(f.id, this.clock + a.span + a.outS + W.restS);
    return true;
  }

  /** The terminal males' chases (`TERRITORY`): each awake and out, by his own times, at a rival near. */
  private chases(seen: readonly LifeFish[], around: (x: number, z: number, r: number) => LifeFish[]): void {
    const T = TERRITORY;
    for (const m of seen) {
      if (m.group !== 'terminal' || !(T.species as readonly string[]).includes(m.species) || (m.awake ?? 0) < 0.5 || (m.feeds ?? 0) < 0.3) continue;
      if (this.acts.has(m.id)) continue;
      const quiet = this.quietUntil.get(m.id);
      if (quiet === undefined) {
        this.quietUntil.set(m.id, this.clock + between(T.againS, hash3(idSalt(m.id), 0, 2_663)));
        continue;
      }
      if (quiet > this.clock) continue;
      const salt = idSalt(m.id) + this.serial;
      const rival = around(m.at.x, m.at.z, T.noticeLengths * m.width).find((r) => r.species === m.species && r.school !== m.school && r.group !== 'young' && !this.acts.has(r.id) && r.at.distanceTo(m.at) < T.noticeLengths * m.width && (r.group === 'terminal' || hash3(idSalt(r.id), Math.floor(this.clock), 2_664) < T.initialOdds));
      if (!rival) continue;
      this.chase(m, rival, 'life', salt);
    }
  }

  /** A male's chase of a rival, and its flight (`TERRITORY`). Whether it was begun. */
  chase(m: LifeFish, rival: LifeFish, staged: 'life' | 'developer', salt = idSalt(m.id) + this.serial): boolean {
    const T = TERRITORY;
    let [ax, az] = [rival.at.x - m.at.x, rival.at.z - m.at.z];
    const len = Math.hypot(ax, az);
    [ax, az] = len > 1e-6 ? [ax / len, az / len] : [1, 0];
    const to = { x: rival.at.x + ax * T.chaseBL * m.width, z: rival.at.z + az * T.chaseBL * m.width };
    const a = this.act(m, 'chase', [{ t: 0, x: m.at.x, y: m.at.y, z: m.at.z }, { t: T.chaseS, x: to.x, y: this.over(m.at.x, m.at.z, to.x, to.z, rival.at.y), z: to.z }], staged);
    a.inS = 0.2;
    a.outS = T.outS;
    a.way = [ax, az];
    a.target = rival.id;
    if (!this.begin(this.paced(a, m))) return false;
    this.quietUntil.set(m.id, this.clock + between(T.againS, hash3(salt, 1, 2_663)));
    const far = between(T.fleeBL, hash3(salt, 2, 2_663)) * rival.width;
    const [x, z] = [rival.at.x + ax * far, rival.at.z + az * far];
    const b = this.act(rival, 'chased', [{ t: 0, x: rival.at.x, y: rival.at.y, z: rival.at.z }, { t: T.fleeAfterS, x: rival.at.x, y: rival.at.y, z: rival.at.z }, { t: T.fleeAfterS + T.chaseS, x, y: this.over(rival.at.x, rival.at.z, x, z, rival.at.y), z }], staged);
    b.inS = T.fleeAfterS + 0.1;
    b.outS = T.outS * 1.3;
    b.way = [ax, az];
    b.threat = m.id;
    this.begin(this.paced(b, rival));
    return true;
  }

  /** By night (`SHELTER`): a share of the young and of the parrotfish to rest in a shelter near, out as they wake. */
  private nights(seen: readonly LifeFish[]): void {
    const anchors = this.world.anchors();
    if (!anchors) return;
    let resting = 0;
    for (const a of this.acts.values()) if (a.night) resting++;
    for (const f of seen) {
      if (resting >= SHELTER.nightMost) return;
      if ((f.awake ?? 1) >= SHELTER.nightAwake || this.acts.has(f.id)) continue;
      if ((f.group !== 'young' && !NIGHT_SHELTERERS.has(f.species)) || OPEN_RESTERS.has(f.species)) continue;
      const salt = idSalt(f.id);
      // Its own night's odds, the same all night (by the clock's hour of nights, not each look).
      if (hash3(salt, Math.floor(this.clock / 600), 2_665) >= SHELTER.nightOdds) continue;
      if ((this.quietUntil.get(f.id) ?? -Infinity) > this.clock) continue;
      const shelter = anchors.near('shelter', f.at.x, f.at.z, SHELTER.nightM, hash3(salt, 1, 2_665), SHELTER.crowd);
      // None near: not tried again for a while.
      if (!shelter) {
        this.quietUntil.set(f.id, this.clock + 30);
        continue;
      }
      anchors.use(shelter, 1);
      const go = 2 + 2 * hash3(salt, 2, 2_665);
      const a = this.act(f, 'rest', [{ t: 0, x: f.at.x, y: f.at.y, z: f.at.z }, { t: 0.7 * go, x: shelter.x, y: this.over(f.at.x, f.at.z, shelter.x, shelter.z, f.at.y), z: shelter.z }, { t: go, x: shelter.x, y: shelter.y + 0.05, z: shelter.z }], 'life');
      a.inS = 1.5;
      a.outS = 3;
      a.anchor = shelter;
      a.feature = shelter.name;
      a.night = true;
      if (this.begin(this.paced(a, f))) resting++;
    }
  }

  /** The herbivores' and the goatfish's bouts (`FEED_STYLES`), each fish by its own times, by day and out. */
  private feeds(seen: readonly LifeFish[]): void {
    for (const f of seen) {
      const style = FEEDERS[f.species];
      if (!style || (f.feeds ?? 0) <= 0.5 || this.acts.has(f.id)) continue;
      const salt = idSalt(f.id);
      const next = this.nextFeed.get(f.id);
      // First seen: its first bout somewhere in its first gap, so a school's fish do not all start at once.
      if (next === undefined) {
        this.nextFeed.set(f.id, this.clock + FEED_STYLES[style].gapS * hash3(salt, 0, 2_666));
        continue;
      }
      if (next > this.clock) continue;
      // Its next gap an even chance's wait (exponential), its mean the style's.
      this.nextFeed.set(f.id, this.clock + FEED_STYLES[style].gapS * -Math.log(1 - 0.999 * hash3(salt, Math.floor(this.clock * 4), 2_667)));
      this.feed(f, style, 'life');
    }
  }

  /** A feeding bout (`FEED_STYLES`): to a place of its food near, its bites there, on to another or not, back to its school. Whether it was begun. */
  feed(f: LifeFish, style: FeedStyle, staged: 'life' | 'developer'): boolean {
    const S = FEED_STYLES[style];
    const anchors = this.world.anchors();
    if (!anchors) return false;
    const salt = idSalt(f.id) + this.serial;
    let place = anchors.near(S.substrate, f.at.x, f.at.z, Math.max(2.5, 4 * f.width), hash3(salt, 0, 2_668));
    if (!place) return false;
    const a = this.act(f, 'feed', [{ t: 0, x: f.at.x, y: f.at.y, z: f.at.z }], staged);
    a.style = style;
    a.tilt = S.tilt;
    a.feature = place.name;
    let t = 0;
    let [px, pz] = [f.at.x, f.at.z];
    for (let k = 0; k < S.places && place; k++) {
      // Its foot where its mouth meets the place, its nose toward it.
      let [wx, wz] = [place.x - px, place.z - pz];
      const wl = Math.hypot(wx, wz);
      [wx, wz] = wl > 1e-6 ? [wx / wl, wz / wl] : [a.way[0], a.way[1]];
      if (k === 0) a.way = [wx, wz];
      const [fx, fz] = [place.x - wx * 0.45 * f.width, place.z - wz * 0.45 * f.width];
      // On a plant, its mouth at the plant's middle height.
      const lift = place.substrate === 'macro' ? 0.5 * place.widthM : 0.02;
      const go = Math.min(3, Math.max(0.4, Math.hypot(fx - px, fz - pz) / (S.speedBL * f.width)));
      // Over the reef to over its place, then down to it.
      const last = a.keys[a.keys.length - 1] as Key;
      a.keys.push({ t: t + 0.7 * go, x: fx, y: this.over(px, pz, fx, fz, last.y), z: fz });
      t += go;
      // At its place, over the ground under its foot and its bites' reach (a rock's edge there sets it on the rock).
      const [rx, rz] = [fx + wx * S.reachBL * f.width, fz + wz * S.reachBL * f.width];
      const at = this.over(fx, fz, rx, rz, place.y + lift, 0.02);
      a.keys.push({ t, x: fx, y: at, z: fz });
      const bites = Math.round(between(S.bites, hash3(salt, k * 5 + 1, 2_668)));
      const arrived = t;
      for (let b = 0; b < bites; b++) {
        a.bites.push({ t, s: S.biteS, reach: S.reachBL * f.width, side: S.sideBL * f.width * (b % 2 === 0 ? 1 : -1) });
        t += S.biteS * (1.15 + 0.3 * hash3(salt, k * 5 + b, 2_669));
      }
      t += between(S.pauseS, hash3(salt, k * 5 + 2, 2_668));
      a.keys.push({ t, x: fx, y: at, z: fz });
      a.faces.push({ from: arrived, to: t, way: [wx, wz] });
      [px, pz] = [fx, fz];
      a.places++;
      if (hash3(salt, k * 5 + 3, 2_668) >= S.moreOdds) break;
      place = anchors.near(S.substrate, place.x, place.z, S.nextM, hash3(salt, k * 5 + 4, 2_668), Infinity, place);
    }
    a.span = t;
    a.inS = 0.6;
    a.outS = 1 + 0.5 * hash3(salt, 9, 2_668);
    return this.begin(this.paced(a, f));
  }

  /**
   * The developer's interactions (the brief's section 13), begun now on the nearest fill of the named kind in view,
   * outside the rules and counting nothing: `graze` a parrotfish on the rock, `nibble` a yellow tang, `probe` a moano kea
   * on the sand, `scatter` the small fish nearest a roi from it, `shelter` a young fish into a coral, `chase` a terminal
   * male after a rival; whether one could be begun. A school's split (`split`), the honu's grazing, the whitetip's rest
   * and the octopus's retreat are their own layers' (`ReefScene.triggerLife`).
   */
  trigger(name: string, camera: THREE.Camera): boolean {
    const fish = this.world.fish().filter((f) => (f.drawn ?? f.shown) >= 0.5 && !this.world.busy().has(f.id));
    const near = (list: readonly LifeFish[]): LifeFish | undefined => [...list].sort((p, q) => p.at.distanceTo(camera.position) - q.at.distanceTo(camera.position))[0];
    const named: Record<string, () => boolean> = {
      graze: () => { const f = near(fish.filter((f) => FEEDERS[f.species] === 'scrape')); return !!f && this.feed(f, 'scrape', 'developer'); },
      nibble: () => { const f = near(fish.filter((f) => f.species === 'yellowTang')) ?? near(fish.filter((f) => FEEDERS[f.species] === 'nibble')); return !!f && this.feed(f, 'nibble', 'developer'); },
      probe: () => { const f = near(fish.filter((f) => f.species === 'moanokea')); return !!f && this.feed(f, 'probe', 'developer'); },
      scatter: () => {
        const roi = near(fish.filter((f) => f.species === 'roi' && f.group !== 'young'));
        if (!roi) return false;
        const prey = [...fish].filter((q) => PREY_OF.get('roi')?.has(q.species)).sort((p, q) => p.at.distanceTo(roi.at) - q.at.distanceTo(roi.at))[0];
        if (!prey) return false;
        const school = fish.filter((m) => m.school === prey.school);
        school.forEach((m, k) => (k === 0 ? this.flee(m, roi.at, roi.id, 'developer', true) : this.alarms.push({ at: this.clock + between(WARINESS.alarmS, hash3(idSalt(m.id), k, 2_661)), id: m.id, from: roi.at.clone(), threat: roi.id })));
        return true;
      },
      shelter: () => {
        const anchors = this.world.anchors();
        for (const f of [...fish.filter((f) => f.group === 'young'), ...fish].sort((p, q) => p.at.distanceTo(camera.position) - q.at.distanceTo(camera.position))) {
          const shelter = anchors?.near('shelter', f.at.x, f.at.z, 2 * SHELTER.nightM, 0.5, SHELTER.crowd);
          if (!shelter) continue;
          const [dx, dz] = [shelter.x - f.at.x, shelter.z - f.at.z];
          const d = Math.max(1e-6, Math.hypot(dx, dz));
          return this.flight(f, shelter, [dx / d, dz / d], d, null, 'developer', true);
        }
        return false;
      },
      chase: () => {
        const males = fish.filter((f) => f.group === 'terminal' && (TERRITORY.species as readonly string[]).includes(f.species));
        for (const m of [...males].sort((p, q) => p.at.distanceTo(camera.position) - q.at.distanceTo(camera.position))) {
          const rival = [...fish].filter((r) => r.species === m.species && r.school !== m.school && r.group !== 'young').sort((p, q) => p.at.distanceTo(m.at) - q.at.distanceTo(m.at))[0];
          if (rival) return this.chase(m, rival, 'developer');
        }
        return false;
      },
    };
    return named[name]?.() ?? false;
  }

  /** The interactions under way, for the developer's overlay (the brief's section 13). */
  debug(): LifeDebug[] {
    return [...this.acts.values()].map((a) => {
      const at = this.keyAt(a, Math.min(a.t, a.span));
      return { id: a.id, species: a.species, behavior: a.kind, priority: a.priority, target: a.target, position: [at.x, at.y, at.z], remainingS: Math.max(0, a.span + a.outS - a.t), threat: a.threat, feature: a.feature, staged: a.staged };
    });
  }

  /** How many are under way, by kind, for the tests and the overlay. */
  counts(): Record<Behavior, number> {
    const n: Record<Behavior, number> = { flee: 0, shelter: 0, rest: 0, chase: 0, chased: 0, feed: 0 };
    for (const a of this.acts.values()) n[a.kind]++;
    return n;
  }

  /** Every fish given back to its school, and every use of a place let go (a habitat left, a world replaced). */
  reset(): void {
    for (const a of [...this.acts.values()]) this.end(a);
    this.alarms = [];
    this.nextFeed.clear();
    this.quietUntil.clear();
    this.lastSeen.clear();
  }
}
